import type { TApplicationStatus } from '@doutorsim/shared/applications'
import type { TPaginationMeta } from '@doutorsim/shared/pagination'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { queries } from '@/features/_lib/react-query'

const DEFAULT_PAGE_SIZE = 20

export interface PaginatedApplicationsQueryFilters {
  mode: 'staff' | 'company' | 'facility'
  companyId?: string
  facilityId?: string
  search?: string
  status?: TApplicationStatus[]
  startDate?: string
  endDate?: string
  pageSize?: number
  filterFacilityIds?: string[]
}

export function usePaginatedApplicationsQuery({
  mode,
  companyId,
  facilityId,
  search,
  status,
  startDate,
  endDate,
  pageSize = DEFAULT_PAGE_SIZE,
  filterFacilityIds,
}: PaginatedApplicationsQueryFilters) {
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined)
  const [cursorStack, setCursorStack] = useState<string[]>([])

  const filtersRef = useRef({ search, status, startDate, endDate, filterFacilityIds })
  useEffect(() => {
    const prev = filtersRef.current
    const changed =
      prev.search !== search ||
      prev.startDate !== startDate ||
      prev.endDate !== endDate ||
      prev.filterFacilityIds !== filterFacilityIds ||
      JSON.stringify(prev.status) !== JSON.stringify(status)

    if (changed) {
      setCurrentCursor(undefined)
      setCursorStack([])
      filtersRef.current = { search, status, startDate, endDate, filterFacilityIds }
    }
  }, [search, status, startDate, endDate, filterFacilityIds])

  const staffQuery = useQuery({
    ...queries.staffApplications.list({
      search,
      status,
      startDate,
      endDate,
      facilityIds: filterFacilityIds?.length ? filterFacilityIds : undefined,
      limit: pageSize,
      cursor: currentCursor,
      direction: currentCursor ? 'forward' : undefined,
    }),
    enabled: mode === 'staff',
    placeholderData: (prev) => prev,
  })

  const companyQuery = useQuery({
    ...queries.companyApplications.list(companyId ?? '', {
      search,
      status,
      startDate,
      endDate,
      facilityIds: filterFacilityIds?.length ? filterFacilityIds : undefined,
      limit: pageSize,
      cursor: currentCursor,
      direction: currentCursor ? 'forward' : undefined,
    }),
    enabled: mode === 'company' && !!companyId,
    placeholderData: (prev) => prev,
  })

  const facilityQuery = useQuery({
    ...queries.applications.list(companyId ?? '', facilityId ?? '', {
      search,
      status,
      startDate,
      endDate,
      limit: pageSize,
      cursor: currentCursor,
      direction: currentCursor ? 'forward' : undefined,
    }),
    enabled: mode === 'facility' && !!facilityId,
    placeholderData: (prev) => prev,
  })

  const activeQuery =
    mode === 'staff' ? staffQuery : mode === 'company' ? companyQuery : facilityQuery
  const data = activeQuery.data?.data ?? []
  const meta: TPaginationMeta | undefined = activeQuery.data?.meta
  const isLoading = activeQuery.isLoading
  const isFetching = activeQuery.isFetching

  const currentPage = cursorStack.length + 1
  const totalCount = meta?.totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const canGoNext = meta?.hasMore ?? false
  const canGoPrevious = cursorStack.length > 0

  const goToNextPage = useCallback(() => {
    if (!meta?.nextCursor || !canGoNext) return
    setCursorStack((prev) => [...prev, currentCursor ?? ''])
    setCurrentCursor(meta.nextCursor)
  }, [meta?.nextCursor, canGoNext, currentCursor])

  const goToPreviousPage = useCallback(() => {
    if (!canGoPrevious) return
    setCursorStack((prev) => {
      const next = [...prev]
      const previousCursor = next.pop()
      setCurrentCursor(previousCursor || undefined)
      return next
    })
  }, [canGoPrevious])

  const goToFirstPage = useCallback(() => {
    setCurrentCursor(undefined)
    setCursorStack([])
  }, [])

  return {
    data,
    isLoading,
    isFetching,
    currentPage,
    totalPages,
    totalCount,
    canGoNext,
    canGoPrevious,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
  }
}
